import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { Ambulance } from "../types/ambulance";
import { useNotification } from "../contexts/NotificationContext";
import notificationSound from "../Assets/notification.mp3";

interface AmbulanceListProps {
  ambulances: Ambulance[];
  loading: boolean;
}

const ListContainer = styled.div`
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: var(--background-secondary);
  box-shadow: var(--box-shadow);
`;

const ListHeader = styled.div`
  padding: 15px 20px;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-primary);
`;

const ListTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: #999;
`;

const ListContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--scrollbar-thumb);
    border-radius: 4px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-secondary);
`;

// Animation for new ambulances
const fadeInBackground = keyframes`
  0% { background-color: rgba(25, 118, 210, 0.2); }
  100% { background-color: transparent; }
`;

// Use data attribute instead of isNew prop to avoid React warnings
const AmbulanceItem = styled.div<{
  $isNew?: boolean;
  $arrived?: boolean;
  $possiblyArrived?: boolean;
}>`
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  background-color: ${(props) =>
    props.$isNew
      ? "var(--highlight-background)"
      : props.$arrived
      ? "var(--success-light-background)"
      : props.$possiblyArrived
      ? "var(--warning-light-background)"
      : "var(--background-secondary)"};
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: background-color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  animation: ${(props) => (props.$isNew ? "fadeIn 0.5s ease-in-out" : "none")};

  @keyframes fadeIn {
    from {
      background-color: var(--highlight-background-bright);
    }
    to {
      background-color: var(--highlight-background);
    }
  }
`;

const NewBadge = styled.div`
  top: 8px;
  right: 8px;
  background: #1976d2;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const ArrivedBadge = styled.div`
  top: 8px;
  right: 8px;
  background: #4caf50;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const PossiblyArrivedBadge = styled.div`
  top: 8px;
  right: 8px;
  background: #ff9800;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
`;

const UnitIdRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UnitId = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary);
`;

const AgencyId = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    if (props.status === "AT_HOSPITAL") return "var(--success-color-light)";
    if (props.status === "POSSIBLY_ARRIVED")
      return "var(--warning-color-light)";
    return "var(--info-color-light)";
  }};
  color: ${(props) => {
    if (props.status === "AT_HOSPITAL") return "var(--success-color-dark)";
    if (props.status === "POSSIBLY_ARRIVED") return "var(--warning-color-dark)";
    return "var(--info-color-dark)";
  }};
`;

const InfoRow = styled.div`
  display: flex;
  color: var(--text-secondary);
  font-size: 14px;
  margin-top: 4px;
`;

const InfoLabel = styled.div`
  min-width: 75px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-weight: 400;
`;

const LocationSource = styled.span<{ source: string }>`
  font-size: 11px;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: ${(props) => {
    if (props.source === "MANUAL") return "var(--warning-color-light)";
    if (props.source === "GEOCODED") return "var(--info-color-light)";
    return "var(--success-color-light)";
  }};
  color: ${(props) => {
    if (props.source === "MANUAL") return "var(--warning-color-dark)";
    if (props.source === "GEOCODED") return "var(--info-color-dark)";
    return "var(--success-color-dark)";
  }};
`;

const LoadingIndicator = styled.div`
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
`;

const LocationIndicator = styled.span<{ available: boolean }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.available ? "var(--success-color)" : "var(--text-disabled)"};
  margin-right: 6px;
`;

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const calculateArrivalTime = (eta: number | null, timestamp?: Date): string => {
  if (!eta) return "";
  
  // If a timestamp is provided, use it as the base time
  // This ensures a fixed arrival time calculation rather than one that shifts
  const baseTime = timestamp ? timestamp : new Date();
  const arrivalTime = new Date(baseTime.getTime() + eta * 1000);
  
  // Format the time in New York timezone
  return arrivalTime.toLocaleTimeString([], { 
    hour: "2-digit", 
    minute: "2-digit",
    timeZone: "America/New_York"
  });
};

const formatEta = (
  ambulance: Ambulance
): { text: string; highlight?: boolean } => {
  if (ambulance.arrived) return { text: "Arrived", highlight: true };
  if (ambulance.possiblyArrived)
    return { text: "Possibly Arrived", highlight: true };
  if (!ambulance.hasLocation) return { text: "Location unavailable" };
  if (!ambulance.eta) return { text: "Calculating route..." };

  // Calculate remaining time by subtracting elapsed time since timestamp
  const originalEta = ambulance.eta; // Original ETA in seconds
  const now = new Date().getTime();
  const timestampMs = ambulance.timestamp.getTime();
  const elapsedSeconds = (now - timestampMs) / 1000;
  
  // Calculate remaining seconds (don't go below 0)
  const remainingSeconds = Math.max(0, originalEta - elapsedSeconds);
  
  // Check if ETA has passed more than 4 minutes ago but status hasn't updated
  const minutesPastEta = (elapsedSeconds - originalEta) / 60;
  if (remainingSeconds === 0 && minutesPastEta >= 4) {
    return { text: "Arrived *Estimated*", highlight: true };
  }
  
  const minutes = Math.ceil(remainingSeconds / 60);

  // Highlight if ETA is less than 5 minutes
  const highlight = minutes < 5;
  
  // If remaining time is 0, show "Imminent"
  if (minutes === 0) {
    return { text: "Imminent", highlight: true };
  }

  if (minutes < 60) {
    return {
      text: `${minutes} min${minutes !== 1 ? "s" : ""}`,
      highlight,
    };
  } else {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return {
      text: `${hours} hr${hours !== 1 ? "s" : ""} ${remainingMinutes} min${
        remainingMinutes !== 1 ? "s" : ""
      }`,
      highlight,
    };
  }
};

const formatElapsedTime = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours} hr${hours !== 1 ? "s" : ""} ${remainingMinutes} min${
      remainingMinutes !== 1 ? "s" : ""
    }`;
  } else if (minutes > 0) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  } else {
    return "Just arrived";
  }
};

// Function to map agency code to agency name
const getAgencyName = (code: string): string => {
  if (!code) return '';
  
  const agencyMap: Record<string, string> = {
    // First batch (1-xx to 3-xx)
    '1-01': 'AMITYVILLE FD (1-01)',
    '1-02': 'BABYLON FD (1-02)',
    '1-03': 'COPIAGUE FD (1-03)',
    '1-04': 'DEER PARK FD (1-04)',
    '1-05': 'EAST FARMINGDALE FD (1-05)',
    '1-06': 'LINDENHURST FD (1-06)',
    '1-07': 'N. AMITYVILLE FD (1-07)',
    '1-08': 'N. BABYLON FD (1-08)',
    '1-09': 'W. BABYLON FD (1-09)',
    '1-10': 'WYANDANCH FD (1-10)',
    '1-11': 'N LINDENHURST FD (1-11)',
    '1-15': 'REPUBLIC AIRPORT (1-15)',
    '1-20': 'WYANDANCH AMB (1-20)',
    '1-22': 'REPUBLIC AIRPORT (1-22)',
    '2-01': 'COLD SPRING HARBOR FD (2-01)',
    '2-02': 'HALESITE FD (2-02)',
    '2-03': 'HUNTINGTON FD (2-03)',
    '2-04': 'HUNTINGTON MANOR FD (2-04)',
    '2-05': 'MELVILLE FD (2-05)',
    '2-06': 'CENTERPORT FD (2-06)',
    '2-07': 'GREENLAWN FD (2-07)',
    '2-08': 'DIX HILLS FD (2-08)',
    '2-09': 'NORTHPORT FD (2-09)',
    '2-10': 'E. NORTHPORT FD (2-10)',
    '2-11': 'COMMACK FD (2-11)',
    '2-12': 'EATONS NECK FD (2-12)',
    '2-13': 'NORTHPORT VA (2-13)',
    '2-15': 'HUNTINGTON COMM. (2-15)',
    '2-16': 'COMMACK AMB (2-16)',
    '2-17': 'NORTHPORT POWER STA. (2-17)',
    '3-01': 'BAY SHORE FD (3-01)',
    '3-02': 'BRENTWOOD FD (3-02)',
    '3-03': 'E. BRENTWOOD FD (3-03)',
    '3-04': 'ISLIP FD (3-04)',
    '3-05': 'E. ISLIP FD (3-05)',
    '3-06': 'ISLIP TERRACE FD (3-06)',
    '3-07': 'CENTRAL ISLIP FD (3-07)',
    '3-08': 'HAUPPAUGE FD (3-08)',
    '3-09': 'GREAT RIVER FD (3-09)',
    '3-10': 'W. SAYVILLE FD (3-10)',
    '3-11': 'SAYVILLE FD (3-11)',
    '3-12': 'BOHEMIA FD (3-12)',
    '3-13': 'LAKELAND FD (3-13)',
    '3-14': 'BAYPORT FD (3-14)',
    '3-15': 'HOLBROOK FD (3-15)',
    '3-16': 'FAIR HARBOR FD (3-16)',
    '3-17': 'WEST ISLIP FD (3-17)',
    '3-18': 'SALTAIRE FD (3-18)',
    '3-20': 'OCEAN BEACH FD (3-20)',
    '3-21': 'KISMET FD (3-21)',
    '3-22': 'MACARTHUR AIRPORT (3-22)',
    '3-24': 'BAY SHORE AMB (3-24)',
    '3-25': 'BRENTWOOD AMB (3-25)',
    '3-26': 'CENTRAL ISLIP AMB (3-26)',
    '3-27': 'EXCHANGE AMB (3-27)',
    '3-28': 'SAYVILLE COMMUNITY (3-28)',
    
    // Second batch (4-xx to 10-xx and special codes)
    '4-01': 'KINGS PARK FD (4-01)',
    '4-02': 'SMITHTOWN FD (4-02)',
    '4-03': 'ST JAMES FD (4-03)',
    '4-04': 'NESCONSET FD (4-04)',
    '4-05': 'NISSEQUOGUE FD (4-05)',
    '5-01': 'BELLPORT FD (5-01)',
    '5-02': 'BLUE POINT FD (5-02)',
    '5-03': 'BROOKHAVEN FD (5-03)',
    '5-04': 'CENTER MORICHES FD (5-04)',
    '5-05': 'CENTEREACH FD (5-05)',
    '5-06': 'CORAM FD (5-06)',
    '5-07': 'E. MORICHES FD (5-07)',
    '5-08': 'EASTPORT FD (5-08)',
    '5-09': 'GORDON HEIGHTS FD (5-09)',
    '5-10': 'HAGERMAN FD (5-10)',
    '5-11': 'HOLTSVILLE FD (5-11)',
    '5-12': 'MASTIC FD (5-12)',
    '5-13': 'MASTIC BEACH FD (5-13)',
    '5-14': 'MEDFORD FD (5-14)',
    '5-15': 'MIDDLE ISLAND FD (5-15)',
    '5-16': 'MANORVILLE FD (5-16)',
    '5-17': 'N. PATCHOGUE FD (5-17)',
    '5-18': 'FARMINGVILLE FD (5-18)',
    '5-19': 'PATCHOGUE FD (5-19)',
    '5-20': 'BROOKHAVEN LAB (5-20)',
    '5-21': 'POINT O\' WOODS FD (5-21)',
    '5-22': 'RIDGE FD (5-22)',
    '5-24': 'RONKONKOMA FD (5-24)',
    '5-25': 'SELDEN FD (5-25)',
    '5-26': 'CHERRY GROVE FD (5-26)',
    '5-27': 'DAVIS PARK FD (5-27)',
    '5-28': 'FIRE ISLAND PINES FD (5-28)',
    '5-29': 'OCEAN BAY PARK FD (5-29)',
    '5-30': 'YAPHANK FD (5-30)',
    '5-32': 'CHERRY GROVE RESP. (5-32)',
    '5-33': 'PORT JEFF AMB (5-33)',
    '5-34': 'MEDFORD AMB (5-34)',
    '5-35': 'SUNY VAC (5-35)',
    '5-37': 'MASTIC AMB (5-37)',
    '5-38': 'SHIRLEY AMB (5-38)',
    '5-39': 'MASTIC BEACH AMB (5-39)',
    '5-40': 'MANORVILLE AMB (5-40)',
    '5-41': 'UNIVERSITY HOSP (5-41)',
    '5-42': 'SOUTH COUNTRY AMB (5-42)',
    '5-47': 'E. MORICHES AMB (5-47)',
    '5-49': 'PATCHOGUE AMB (5-49)',
    '5A-01': 'PORT JEFF FD (5A-01)',
    '5A-02': 'SETAUKET FD (5A-02)',
    '5A-03': 'STONY BROOK FD (5A-03)',
    '5A-04': 'TERRYVILLE FD (5A-04)',
    '5A-05': 'MOUNT SINAI FD (5A-05)',
    '5A-06': 'SOUND BEACH FD (5A-06)',
    '5A-07': 'ROCKY POINT FD (5A-07)',
    '5A-09': 'MILLER PLACE FD (5A-09)',
    '6-01': 'JAMESPORT FD (6-01)',
    '6-02': 'RIVERHEAD FD (6-02)',
    '6-03': 'WADING RIVER FD (6-03)',
    '6-05': 'RIVERHEAD AMB (6-05)',
    '7-01': 'SAG HARBOR FD (7-01)',
    '7-02': 'BRIDGEHAMPTON FD (7-02)',
    '7-03': 'SOUTHAMPTON FD (7-03)',
    '7-04': 'NORTH SEA FD (7-04)',
    '7-05': 'HAMPTON BAYS FD (7-05)',
    '7-06': 'E. QUOGUE FD (7-06)',
    '7-07': 'QUOGUE FD (7-07)',
    '7-08': 'WESTHAMPTON FD (7-08)',
    '7-09': 'FLANDERS FD (7-09)',
    '7-10': 'GABRESKI AIRBASE (7-10)',
    '7-12': 'HAMPTON BAYS AMB (7-12)',
    '7-14': 'SOUTHAMPTON AMB (7-14)',
    '7-15': 'SAG HARBOR AMB (7-15)',
    '7-16': 'WESTHAMPTON AMB (7-16)',
    '7-17': 'FLANDERS AMB (7-17)',
    '7-18': 'SOUTHAMPTON VILL AMB (7-18)',
    '8-01': 'ORIENT FD (8-01)',
    '8-02': 'EAST MARION FD (8-02)',
    '8-03': 'GREENPORT FD (8-03)',
    '8-04': 'SOUTHOLD FD (8-04)',
    '8-05': 'CUTCHOGUE FD (8-05)',
    '8-06': 'MATTITUCK FD (8-06)',
    '8-07': 'FISHERS ISLAND (8-07)',
    '8-08': 'PLUM ISLAND (8-08)',
    '9-01': 'EASTHAMPTON FD (9-01)',
    '9-02': 'AMAGANSETT FD (9-02)',
    '9-03': 'MONTAUK FD (9-03)',
    '9-04': 'SPRINGS FD (9-04)',
    '10-01': 'SHELTER ISLAND FD (10-1)',
    '10-04': 'SHELTER ISLAND AMB (10-4)',
    'NASS': 'NASSAU COUNTY',
    'SCFM': 'SC FIRE MARSHALS',
    'BARRIER': 'BARRIER BEACH',
    'SCFRES': 'SUFFOLK FRES',
    '9-99': 'CHESTERTOWN'
  };

  return agencyMap[code] || code;
};

const AmbulanceList: React.FC<AmbulanceListProps> = ({
  ambulances,
  loading,
}) => {
  // Add state to force re-render for countdown
  const [, setForceUpdate] = useState(0);
  const prevAmbulancesRef = useRef<Ambulance[]>([]);
  const notificationAudioRef = useRef<HTMLAudioElement | null>(null);
  const { isMuted } = useNotification();

  // Initialize audio element for notification
  useEffect(() => {
    notificationAudioRef.current = new Audio(notificationSound);
  }, []);

  // Set up interval to update component every second
  useEffect(() => {
    const timer = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Play notification sound when new ambulances arrive
  useEffect(() => {
    // Check if there's any new ambulance in the current list
    const hasNewAmbulance = ambulances.some(ambulance => ambulance.isNew);
    
    // Check if the number of ambulances increased (new ambulance added)
    const prevCount = prevAmbulancesRef.current.length;
    const currentCount = ambulances.length;
    
    // Play sound if there's a new ambulance that wasn't in the previous list and sound is not muted
    if (hasNewAmbulance && currentCount > prevCount && !isMuted) {
      notificationAudioRef.current?.play().catch(error => {
        console.error("Error playing notification sound:", error);
      });
    }
    
    // Update the reference to current ambulances for next comparison
    prevAmbulancesRef.current = [...ambulances];
  }, [ambulances, isMuted]);

  if (loading) {
    return (
      <ListContainer>
        <ListHeader>
          <ListTitle>Ambulances En Route</ListTitle>
        </ListHeader>
        <LoadingIndicator>Loading ambulance data...</LoadingIndicator>
      </ListContainer>
    );
  }

  // Sort ambulances: new ones first, then by ETA
  const sortedAmbulances = [...ambulances].sort((a, b) => {
    // Arrived ambulances go to the bottom
    if (a.arrived && !b.arrived) return 1;
    if (!a.arrived && b.arrived) return -1;

    // For ambulances with the same arrival status:
    if (!a.arrived && !b.arrived) {
      // New ambulances come first for non-arrived ambulances
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;

      // Then sort by ETA if available
      if (a.eta && b.eta) return a.eta - b.eta;
      if (a.eta && !b.eta) return -1;
      if (!a.eta && b.eta) return 1;
    } else if (a.arrived && b.arrived) {
      // For arrived ambulances, show most recently arrived first
      return (b.arrivalTime?.getTime() || 0) - (a.arrivalTime?.getTime() || 0);
    }

    return 0;
  });

  return (
    <ListContainer>
      <ListHeader>
        <ListTitle>Ambulances En Route</ListTitle>
      </ListHeader>
      <ListContent>
        {sortedAmbulances.length === 0 ? (
          <EmptyState>
            No ambulances are currently en route to Huntington Hospital
          </EmptyState>
        ) : (
          sortedAmbulances.map((ambulance) => {
            const eta = formatEta(ambulance);

            return (
              <AmbulanceItem
                key={ambulance.id}
                $isNew={ambulance.isNew}
                $arrived={ambulance.arrived}
                $possiblyArrived={ambulance.possiblyArrived}
              >
                {ambulance.isNew && <NewBadge>New</NewBadge>}
                {ambulance.arrived && <ArrivedBadge>Arrived</ArrivedBadge>}
                {ambulance.possiblyArrived && (
                  <PossiblyArrivedBadge>Arrived *Estimated*</PossiblyArrivedBadge>
                )}
                <UnitIdRow>
                  <UnitId>
                     <AgencyId>{getAgencyName(ambulance.Agency)}</AgencyId>
                    <LocationIndicator available={ambulance.hasLocation} />
                    Unit: {ambulance.unitId} <br></br>
                    Call Type: {ambulance.callType}
                  </UnitId>

                  <StatusBadge status={ambulance.status}>
                    {ambulance.status}
                  </StatusBadge>
                </UnitIdRow>
                <InfoRow>
                  <InfoLabel>ETA:</InfoLabel>
                  <InfoValue>{eta.text}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Arrival Time: </InfoLabel>
                  <InfoValue>
                    {ambulance.status === "ARRIVED" 
                      ? formatTime(ambulance.timestamp) 
                      : calculateArrivalTime(ambulance.eta, ambulance.timestamp)}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Last Updated:</InfoLabel>
                  <InfoValue>{formatTime(ambulance.timestamp)}</InfoValue>
                </InfoRow>
              </AmbulanceItem>
            );
          })
        )}
      </ListContent>
    </ListContainer>
  );
};

export default AmbulanceList;
